import {
    Button,
    makeStyles,
    Menu,
    MenuButton,
    MenuDivider,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { ArrowDownload20Regular, DocumentPdf20Regular, DocumentTable20Regular, Print20Regular } from '@fluentui/react-icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISelectedTaxReturnsAndFilters } from '../../data-types/exportReports';
import { TaxReturnTOCItemWithRequiredNodeKey } from '../../data-types/printReportModalTOCItem';
import { useDefaultModalStyles } from '../../hooks/useDefaultModalStyles';
import { useExportReports } from '../../hooks/useExportReports';
import { useToastMessage } from '../../hooks/useToastMessage';
import { ReportListRequest } from '../../model';
import Modal from './ModalV9';

export interface IExportReportButtonProps {
    label?: string;
    disabled?: boolean;
    includeExportList?: boolean;
    getReportsToExport: () => ISelectedTaxReturnsAndFilters;
    onPrintRequest?: (initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[]) => void;
}

interface IErrorModalProps {
    isErrorModalVisible: boolean;
    hideErrorModal: () => void;
    message: string;
}

const useMenuItemStyles = makeStyles({
    item: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
        ':hover': { color: tokens.colorPaletteRoyalBlueBorderActive },
    },
    processingSpinner: {
        paddingLeft: '5px',
        opacity: 0.5,
        fontSize: '10px',
    },
});

const ErrorModal = ({ isErrorModalVisible, hideErrorModal, message }: IErrorModalProps) => {
    const { t } = useTranslation();
    const { button } = useDefaultModalStyles();
    return (
        <Modal
            headerContent={t('Error')}
            bodyContent={<div style={{ minHeight: 50 }}>{message}</div>}
            isOpen={isErrorModalVisible}
            onClose={hideErrorModal}
            buttons={
                <Button appearance='primary' size='small' aria-label={t('ok').toString()} onClick={hideErrorModal} className={button}>
                    {t('ok').toString()}
                </Button>
            }
        />
    );
};

const ExportReportButton: React.FC<IExportReportButtonProps> = ({
    label,
    getReportsToExport,
    disabled,
    includeExportList,
    onPrintRequest,
}) => {
    const { t } = useTranslation();
    const { showToast } = useToastMessage();
    label = label ?? t('export').toString();

    const { exportToExcelMutation, exportToPdfMutation, setExportedReports, isProcessing, exportTaxReturnListToExcelMutation } =
        useExportReports();
    const [isErrorModalVisible, { setTrue: showErrorModal, setFalse: hideErrorModalState }] = useBoolean(false);
    const exportButtonRef = useRef<HTMLButtonElement>(null);

    // The modal is opened from a menu item that unmounts with the menu, so the dialog has no trigger to restore
    // focus to. Return it to the Export button explicitly once the dialog has closed.
    const hideErrorModal = () => {
        hideErrorModalState();
        window.setTimeout(() => exportButtonRef.current?.focus(), 0);
    };

    const [errorMessage, setErrorMessage] = useState<string>();

    const getRequestsWithTimezone = (reports: ISelectedTaxReturnsAndFilters) => {
        return reports.selectedTaxReturns?.map((taxReturn) => {
            taxReturn.timezoneOffsetMinutes = new Date().getTimezoneOffset();
            return taxReturn;
        });
    };

    const showExportError = (message?: string) => {
        setExportedReports([]);
        setErrorMessage(message);
        showErrorModal();
    };

    const getRequestsForExport = (validateExecutedReturns?: boolean): ISelectedTaxReturnsAndFilters | undefined => {
        try {
            const reports = getReportsToExport();

            let droppedCount = 0;
            if (validateExecutedReturns) {
                const originalCount = reports.selectedTaxReturns?.length ?? 0;
                const hasUnloadedCalculation = reports.selectedTaxReturns?.some((r) => r.canExecute === false);
                // Rows that cannot be exported (calculation not loaded, or never run) are dropped, not fatal.
                reports.selectedTaxReturns = reports.selectedTaxReturns?.filter((r) => r.canExecute !== false && r.executed);
                if (originalCount > 0 && !reports.selectedTaxReturns?.length) {
                    showExportError(
                        t(
                            hasUnloadedCalculation ? 'noExportableReportsSelectedError' : 'notExecutedReportSelectedForExportError'
                        ).toString()
                    );
                    return undefined;
                }
                droppedCount = originalCount - (reports.selectedTaxReturns?.length ?? 0);
            }

            reports.selectedTaxReturns = getRequestsWithTimezone(reports);

            ensureAllSelectedReportsHaveExecuted(reports, validateExecutedReturns, t('notExecutedReportSelectedForExportError'));

            if (droppedCount > 0) {
                showToast(
                    t('partialExportReportsDroppedTitle').toString(),
                    <span>{t('partial_export_reports_dropped', { count: droppedCount }).toString()}</span>,
                    'warning'
                );
            }

            setExportedReports(reports.selectedTaxReturns);

            return reports;
        } catch {
            showExportError(undefined);
            return undefined;
        }
    };

    const exportToExcel = () => {
        const requests = getRequestsForExport(true);

        if (requests) {
            exportToExcelMutation({ data: requests.selectedTaxReturns || [] });
        }
    };

    const exportToPdf = () => {
        const requests = getRequestsForExport(true);

        if (requests) {
            exportToPdfMutation({ data: requests.selectedTaxReturns || [] });
        }
    };

    const exportTaxReturnToExcel = () => {
        const requests = getRequestsForExport();

        const adaptedRequests: ReportListRequest = {
            ...requests?.filters,
            timeZoneOffSetMinutes: new Date().getTimezoneOffset(),
        };
        if (requests?.selectedTaxReturns?.length) {
            adaptedRequests.selectedIds = requests.selectedTaxReturns.map((item) => item.id) as number[];
        }

        exportTaxReturnListToExcelMutation({ data: adaptedRequests });
    };

    const { item, processingSpinner } = useMenuItemStyles();

    const menuExportSelectedReportItems = (
        <>
            <MenuItem icon={<DocumentTable20Regular />} className={item} onClick={exportToExcel}>
                {t('exportReportAsXlsx').toString()}
            </MenuItem>
            <MenuItem icon={<DocumentPdf20Regular />} className={item} onClick={exportToPdf}>
                {t('exportReportAsPdf').toString()}
            </MenuItem>
            {onPrintRequest && (
                <MenuItem icon={<Print20Regular />} className={item} onClick={() => onPrintRequest?.()}>
                    {t('printDownloadPDF').toString()}
                </MenuItem>
            )}
        </>
    );

    const menuList = <MenuList>{menuExportSelectedReportItems}</MenuList>;
    const menuWithExportList = (
        <MenuList>
            <MenuGroup>
                <MenuGroupHeader>{t('exportSelectedReports').toString()}</MenuGroupHeader>
                {menuExportSelectedReportItems}
            </MenuGroup>
            <MenuDivider />
            <MenuGroup>
                <MenuGroupHeader>{t('exportList').toString()}</MenuGroupHeader>
                <MenuItem icon={<DocumentTable20Regular />} className={item} onClick={exportTaxReturnToExcel}>
                    {t('exportReportAsXlsx').toString()}
                </MenuItem>
            </MenuGroup>
        </MenuList>
    );

    return (
        <>
            <ErrorModal
                hideErrorModal={hideErrorModal}
                isErrorModalVisible={isErrorModalVisible}
                message={errorMessage ?? t('notExecutedReportSelectedForExportError')}
            />
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <MenuButton
                        ref={exportButtonRef}
                        aria-label={label}
                        icon={<ArrowDownload20Regular />}
                        disabled={Boolean(disabled || isProcessing)}
                    >
                        {isProcessing ? t('working').toString() : label}
                        {isProcessing && <Spinner size='extra-tiny' className={processingSpinner} />}
                    </MenuButton>
                </MenuTrigger>
                <MenuPopover>{includeExportList ? menuWithExportList : menuList}</MenuPopover>
            </Menu>
        </>
    );
};

export const ensureAllSelectedReportsHaveExecuted = (
    reports: ISelectedTaxReturnsAndFilters,
    validateExecutedReturns = false,
    errorMessage: string
) => {
    if (validateExecutedReturns && !reports?.selectedTaxReturns?.every((report) => report.executed)) {
        throw errorMessage;
    }
};
export { ExportReportButton as default };
